import React, { useState } from 'react';
import ClinicLayout from './ClinicLayout';
import { ChevronLeft, ChevronRight, Folder, Mail, Send, Trash2 } from 'lucide-react';

const ClinicDesktop = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <ClinicLayout activeTab="desktop">
      <div className="p-4 pr-20" data-testid="clinic-desktop">
        <div className="grid grid-cols-3 gap-4">
          {/* Left Column - Appointments */}
          <div className="col-span-1">
            <div className="border border-gray-300">
              {/* Header */}
              <div className="bg-gradient-to-b from-[#4a5568] to-[#2d3748] text-white px-3 py-2 text-sm font-medium">
                Appointments
              </div>
              {/* Content */}
              <div className="p-3 bg-[#e8f4e8]">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs w-24">Office:</label>
                    <select className="flex-1 border border-gray-400 text-xs px-1 py-0.5">
                      <option>Office1</option>
                    </select>
                    <button className="text-xs">📋</button>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs w-24">Provider/Staff:</label>
                    <select className="flex-1 border border-gray-400 text-xs px-1 py-0.5">
                      <option>-- All --</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <select className="border border-gray-400 text-xs px-1 py-0.5 w-16">
                      <option>15 min</option>
                      <option>30 min</option>
                    </select>
                    <button className="px-1"><ChevronLeft className="w-4 h-4" /></button>
                    <span className="text-xs">{formatDate(selectedDate)}</span>
                    <button className="px-1"><ChevronRight className="w-4 h-4" /></button>
                    <button className="text-xs">📅</button>
                  </div>
                </div>
                <div className="mt-4 h-32 flex items-center justify-center text-gray-500 text-sm">
                  -- CLOSED --
                </div>
              </div>
            </div>
          </div>

          {/* Center Column - Messages */}
          <div className="col-span-1">
            <div className="border border-gray-300">
              {/* Header */}
              <div className="bg-gradient-to-b from-[#4a5568] to-[#2d3748] text-white px-3 py-2 text-sm font-medium">
                Messages - Inbox
              </div>
              {/* Content */}
              <div className="p-3 bg-white">
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <label className="w-20">Date Received:</label>
                    <select className="border border-gray-400 px-1 py-0.5">
                      <option>By Date</option>
                    </select>
                    <input type="text" className="border border-gray-400 px-1 py-0.5 w-8" />
                    <span>/</span>
                    <input type="text" className="border border-gray-400 px-1 py-0.5 w-8" />
                    <span>/</span>
                    <input type="text" className="border border-gray-400 px-1 py-0.5 w-12" />
                    <button className="border border-gray-400 px-2 py-0.5">Show All</button>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="w-20">Search For:</label>
                    <select className="border border-gray-400 px-1 py-0.5">
                      <option>From Last Name</option>
                    </select>
                    <select className="border border-gray-400 px-1 py-0.5">
                      <option>Starts With</option>
                    </select>
                    <input type="text" className="border border-gray-400 px-1 py-0.5 flex-1" />
                    <button className="border border-gray-400 px-2 py-0.5 bg-gray-100">Search</button>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="w-20">Flag Status:</label>
                    <select className="border border-gray-400 px-1 py-0.5">
                      <option>-- All --</option>
                    </select>
                    <div className="flex-1"></div>
                    <span className="text-[#2563eb] cursor-pointer">Compose New ▶</span>
                    <span className="text-red-600 cursor-pointer ml-2">Delete</span>
                    <span className="text-[#2563eb] cursor-pointer ml-2">Move To ▶</span>
                  </div>
                </div>
                
                <div className="mt-3 text-xs text-center text-gray-500 py-2 border-t">
                  There are no data records to display.
                </div>

                {/* Folders */}
                <div className="mt-3 text-xs">
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">⊟</span>
                    <Folder className="w-4 h-4 text-yellow-500" />
                    <span>Folders</span>
                  </div>
                  <div className="ml-6 space-y-1 mt-1">
                    <div className="flex items-center gap-1 text-[#2563eb]">
                      <Mail className="w-3 h-3" />
                      <span>Inbox</span>
                    </div>
                    <div className="flex items-center gap-1 text-[#2563eb]">
                      <Send className="w-3 h-3" />
                      <span>Sent Items</span>
                    </div>
                    <div className="flex items-center gap-1 text-[#2563eb]">
                      <Trash2 className="w-3 h-3" />
                      <span>Deleted Items</span>
                    </div>
                  </div>
                  <div className="text-[#2563eb] mt-2 cursor-pointer">New Folder</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Tasks */}
          <div className="col-span-1">
            <div className="border border-gray-300">
              {/* Header */}
              <div className="bg-gradient-to-b from-[#1e3a5f] to-[#1a365d] text-white px-3 py-2 text-sm font-medium">
                Tasks
              </div>
              {/* Content */}
              <div className="p-3 bg-white">
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <label className="w-20">Due Date:</label>
                    <select className="border border-gray-400 px-1 py-0.5">
                      <option>By Date</option>
                    </select>
                    <input type="text" className="border border-gray-400 px-1 py-0.5 w-8" />
                    <input type="text" className="border border-gray-400 px-1 py-0.5 w-8" />
                    <input type="text" className="border border-gray-400 px-1 py-0.5 w-12" />
                    <button className="border border-gray-400 px-2 py-0.5">Show All</button>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="w-20">Search For:</label>
                    <select className="border border-gray-400 px-1 py-0.5">
                      <option>Requestor First Name</option>
                    </select>
                    <select className="border border-gray-400 px-1 py-0.5">
                      <option>Starts With</option>
                    </select>
                    <input type="text" className="border border-gray-400 px-1 py-0.5 flex-1" />
                    <button className="border border-gray-400 px-2 py-0.5 bg-gray-100">Search</button>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="w-12">Tasks:</label>
                    <select className="border border-gray-400 px-1 py-0.5">
                      <option>Assigned To Me</option>
                    </select>
                    <label className="ml-2">Status:</label>
                    <select className="border border-gray-400 px-1 py-0.5">
                      <option>-- All Open --</option>
                    </select>
                    <label className="ml-2">Priority:</label>
                    <select className="border border-gray-400 px-1 py-0.5">
                      <option>-- All --</option>
                    </select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button className="border border-gray-400 px-2 py-0.5 bg-gray-100">Add New</button>
                    <span className="text-[#2563eb] cursor-pointer">Select Actions ▶</span>
                  </div>
                </div>

                {/* Tasks Table */}
                <div className="mt-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#1e3a5f] text-white">
                        <th className="p-1 border border-gray-400 w-6"><input type="checkbox" /></th>
                        <th className="p-1 border border-gray-400">Status</th>
                        <th className="p-1 border border-gray-400">Subject</th>
                        <th className="p-1 border border-gray-400">Priority</th>
                        <th className="p-1 border border-gray-400">Due Date</th>
                        <th className="p-1 border border-gray-400">Requestor</th>
                        <th className="p-1 border border-gray-400">Assigned To</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan="7" className="text-center py-8 text-gray-500">
                          
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="mt-3 flex items-center justify-center gap-2 text-xs">
                  <span>|◀ ◀◀</span>
                  <span>Page</span>
                  <input type="text" className="border border-gray-400 w-8 text-center" defaultValue="1" />
                  <span>of 0</span>
                  <span>▶▶ ▶|</span>
                  <select className="border border-gray-400 px-1">
                    <option>10</option>
                  </select>
                  <span className="ml-4 text-gray-500">There are no data records to display.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ClinicLayout>
  );
};

export default ClinicDesktop;
